import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Form, Input, InputNumber, message, Radio } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import type { TTextVisitRefundDetailItem, TApproveRefundRequest } from 'types/textVisitRefund';
import { ETextVisitRefundStatus } from 'types/textVisitCase';
import { approveTextVisitRefund, getTextVisitRefundDetail, updateTextVisitRefundNote } from 'api/operation/textVisitRefund';
import { DEFAULT_TEXT_VISIT_AMOUNT } from 'constants/textVisit';
import s from './s.module.less';

type RefundProps = {
    refundId?: number; // optional: existing refund request id
    refundStatus?: string | null; // optional: refund status from parent
    // callback after submit, let parent decide: go next or return home
    onSubmitted?: (action: 'next' | 'home') => void;
    isLastItem?: boolean; // whether this is the last pending review request
    hasRefundPermission?: boolean; // whether user has permission to review refund
};

const approvalOptions = [
    { label: 'Approve full refund', subLabel: 'Return the full amount to the patient.', value: ETextVisitRefundStatus.APPROVE_FULL },
    { label: 'Approve partial refund', subLabel: 'Refund a custom amount to the patient.', value: ETextVisitRefundStatus.APPROVE_PARTIAL },
    { label: 'Deny refund', subLabel: 'No refund will be returned to the patient.', value: ETextVisitRefundStatus.DENIED },
];

const Refund: React.FC<RefundProps> = ({ refundId, refundStatus, onSubmitted, isLastItem = false, hasRefundPermission = true }) => {
    const [loading, setLoading] = useState(false);
    const [detail, setDetail] = useState<TTextVisitRefundDetailItem | null>(
        null,
    );
    const [form] = Form.useForm();
    const [editForm] = Form.useForm();
    const [isEditingNotes, setIsEditingNotes] = useState(false);
    const amountInputRef = useRef<HTMLInputElement | null>(null);
    const [submitAction, setSubmitAction] = useState<'next' | 'home'>('home');

    // const hasRefund = useMemo(() => !!detail || typeof refundId === 'number', [detail, refundId]);
    const hasRefund = true;

    // 判断是否已审批
    const isApproved = refundStatus !== ETextVisitRefundStatus.PENDING_REVIEW;

    const fetchDetail = useCallback(async () => {
        if (typeof refundId !== 'number' || !refundId) return;
        // 如果 refundStatus 为 PENDING_REVIEW，不需要调用接口
        if (refundStatus === ETextVisitRefundStatus.PENDING_REVIEW) return;
        setLoading(true);
        try {
            const res = await getTextVisitRefundDetail(refundId);
            // res.data 是 { code, message, data: {...} } 结构
            // 所以需要访问 res.data.data 才能获取实际的 detail 数据
            if (res?.data?.data) {
                setDetail(res.data.data);
            }
        } catch (err) {
            message.error('Failed to load refund details.');
        } finally {
            setLoading(false);
        }
    }, [refundId, refundStatus]);

    useEffect(() => {
        fetchDetail();
    }, [fetchDetail]);

    // Reset decision form when switching to a different refund task
    useEffect(() => {
        // Clear decision form for a fresh review when moving to next item
        form.resetFields();
        setIsEditingNotes(false);
        // also reset action to default 'home'
        setSubmitAction('home');
    }, [refundId, refundStatus, form]);

    // Auto-focus amount input when selecting partial refund
    const approvalStatus = Form.useWatch?.('approvalStatus', form);
    useEffect(() => {
        if (approvalStatus === ETextVisitRefundStatus.APPROVE_PARTIAL) {
            // Defer focus until after nested field mounts
            setTimeout(() => {
                amountInputRef.current?.focus?.();
            }, 0);
        }
    }, [approvalStatus]);

    const handleApprove = async (values: {
        approvalStatus: string;
        approveRefundAmount?: number;
        approvalReason?: string;
        notesInternal?: string;
        notesToPatient?: string;
        notesToProvider?: string;
    }) => {
        if (!detail && typeof refundId !== 'number') {
            message.warning('No refund request found.');
            return;
        }
        const payload: TApproveRefundRequest = {
            refundRequestId: refundId as number,
            approvalStatus: values.approvalStatus,
            approveRefundAmount: Number(values.approveRefundAmount || 0),
            approvalReason: values.approvalReason || '',
            notesInternal: values.notesInternal || '',
            notesToPatient: values.notesToPatient || '',
            notesToProvider: values.notesToProvider || '',
        };
        setLoading(true);
        try {
            const result = await approveTextVisitRefund(payload);
            if (!result?.error) {
                message.success('Refund decision submitted successfully.');
                // notify parent for next steps
                onSubmitted?.(submitAction || 'home');
            } else {
                message.error(result.error || 'Action failed. Please try again.');
            }
        } catch (err: unknown) {
            const error = err as { message?: string };
            message.error(error?.message || 'Action failed');
        } finally {
            setLoading(false);
        }
    };

    // 获取审批决策的显示文本
    const getDecisionText = () => {
        if (!detail) return '';
        switch (detail.approvalStatus) {
            case ETextVisitRefundStatus.APPROVE_FULL:
                return 'Full refund approved';
            case ETextVisitRefundStatus.APPROVE_PARTIAL:
                return 'Partial refund approved';
            case ETextVisitRefundStatus.DENIED:
                return 'Refund denied';
            default:
                return '';
        }
    };

    // 获取退款金额说明
    const getRefundAmountText = () => {
        if (!detail) return '';
        if (detail.approvalStatus === ETextVisitRefundStatus.DENIED) {
            return 'No refund will be returned to the patient.';
        }
        if (detail.approvalStatus === ETextVisitRefundStatus.APPROVE_FULL) {
            return 'Full amount will be returned to the patient.';
        }
        return `$${detail.approveRefundAmount?.toFixed(2) || '0.00'} will be returned to the patient.`;
    };

    // 处理编辑 internal notes
    const handleEditNotes = () => {
        setIsEditingNotes(true);
        editForm.setFieldsValue({ notesInternal: detail?.notesInternal || '' });
    };

    // 取消编辑
    const handleCancelEdit = () => {
        setIsEditingNotes(false);
        editForm.resetFields();
    };

    // 保存编辑的 notes
    const handleSaveNotes = async () => {
        if (!detail) return;
        const values = await editForm.validateFields();
        setLoading(true);
        try {
            await updateTextVisitRefundNote({
                refundRequestId: detail.id,
                notesInternal: values.notesInternal || '',
            });
            message.success('Internal notes saved successfully.');
            setIsEditingNotes(false);
            fetchDetail();
        } catch (err) {
            message.error('Failed to update notes. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={s.wrapper}>
            <div className={s.container}>
                {/* Header Section */}
                <div className={s.headerSection}>
                    <div className={s.header}>
                        {isApproved
                            ? 'Refund request decision'
                            : 'Review refund request'}
                    </div>
                    {!isApproved && (
                        <div className={s.subHeader}>
                            Please select a reason for the refund decision. You
                            can also leave internal notes for record-keeping and
                            add messages for the patient and provider.
                        </div>
                    )}
                </div>

                {/* Content Section */}
                <div className={s.contentSection}>
                    {!hasRefund && (
                        <div className={s.empty}>
                            No refund request for this case yet.
                        </div>
                    )}

                    {hasRefund && isApproved && detail && (
                        <>
                            {/* Decision Card */}
                            <div className={s.decisionCard}>
                                <div className={s.decisionInfo}>
                                    <div className={s.decisionTitle}>
                                        {getDecisionText()}
                                    </div>
                                    <div className={s.decisionDesc}>
                                        {getRefundAmountText()}
                                    </div>
                                </div>
                            </div>

                            {/* Field Sections */}
                            <div className={s.fieldSection}>
                                <div className={s.fieldHeader}>
                                    <div className={s.labelRow}>
                                        <div className={s.label}>
                                            Internal notes{' '}
                                            <span className={s.optional}>
                                                (optional)
                                            </span>
                                        </div>
                                    </div>
                                    {isEditingNotes ? (
                                        <div className={s.editActionsInline}>
                                            <Button
                                                onClick={handleCancelEdit}
                                                className={s.secondaryWhite}
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                type="primary"
                                                htmlType="submit"
                                                loading={loading}
                                                className={s.primaryGreen}
                                            >
                                                Save
                                            </Button>
                                        </div>
                                    ) : (
                                        <Button
                                            icon={<EditOutlined />}
                                            onClick={handleEditNotes}
                                            className={s.editButton}
                                            disabled={!hasRefundPermission}
                                        >
                                            Edit
                                        </Button>
                                    )}
                                </div>
                                {isEditingNotes ? (
                                    <Form
                                        form={editForm}
                                        onFinish={handleSaveNotes}
                                        className={s.editForm}
                                    >
                                        <Form.Item
                                            name="notesInternal"
                                            className={s.noMargin}
                                        >
                                            <Input.TextArea
                                                rows={5}
                                                maxLength={1000}
                                                autoFocus
                                            />
                                        </Form.Item>
                                    </Form>
                                ) : (
                                    <div className={s.notesContent}>
                                        {detail.notesInternal ? (
                                            detail.notesInternal
                                        ) : (
                                            <span
                                                className={s.notesPlaceholder}
                                            >
                                                -
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className={s.fieldSection}>
                                <div className={s.labelRow}>
                                    <div className={s.label}>
                                        Notes to patient{' '}
                                        <span className={s.optional}>
                                            (optional)
                                        </span>
                                    </div>
                                </div>
                                <div className={s.notesContent}>
                                    {detail.notesToPatient ? (
                                        detail.notesToPatient
                                    ) : (
                                        <span className={s.notesPlaceholder}>
                                            -
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className={s.fieldSection}>
                                <div className={s.labelRow}>
                                    <div className={s.label}>
                                        Notes to provider{' '}
                                        <span className={s.optional}>
                                            (optional)
                                        </span>
                                    </div>
                                </div>
                                <div className={s.notesContent}>
                                    {detail.notesToProvider ? (
                                        detail.notesToProvider
                                    ) : (
                                        <span className={s.notesPlaceholder}>
                                            -
                                        </span>
                                    )}
                                </div>
                            </div>
                        </>
                    )}

                    {hasRefund && !isApproved && hasRefundPermission && (
                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={handleApprove}
                            className={s.form}
                        >
                            {/* Radio Group */}
                            <Form.Item
                                name="approvalStatus"
                                className={s.radioGroup}
                                rules={[
                                    {
                                        required: true,
                                        message: 'Select a decision',
                                    },
                                ]}
                            >
                                <Radio.Group className={s.listGroup} disabled={!hasRefundPermission}>
                                    {approvalOptions.map((opt) => {
                                        const isPartialOption =
                                            opt.value ===
                                            ETextVisitRefundStatus.APPROVE_PARTIAL;
                                        return (
                                            <div
                                                key={opt.value}
                                                className={s.listItemWrapper}
                                            >
                                                <div className={s.listItem}>
                                                    <Radio
                                                        value={opt.value}
                                                        className={s.radioLabel}
                                                    >
                                                        <div
                                                            className={
                                                                s.listContent
                                                            }
                                                        >
                                                            <div
                                                                className={
                                                                    s.listTitle
                                                                }
                                                            >
                                                                {opt.label}
                                                            </div>
                                                            <div
                                                                className={
                                                                    s.listDesc
                                                                }
                                                            >
                                                                {opt.subLabel}
                                                            </div>
                                                        </div>
                                                    </Radio>
                                                </div>
                                                {isPartialOption && (
                                                    <Form.Item
                                                        shouldUpdate
                                                        noStyle
                                                    >
                                                        {({
                                                            getFieldValue,
                                                        }) => {
                                                            const isPartialRefund =
                                                                getFieldValue(
                                                                    'approvalStatus',
                                                                ) ===
                                                                ETextVisitRefundStatus.APPROVE_PARTIAL;
                                                            return isPartialRefund ? (
                                                                <div
                                                                    className={
                                                                        s.nestedContent
                                                                    }
                                                                >
                                                                    <Form.Item
                                                                        name="approveRefundAmount"
                                                                        rules={[
                                                                            {
                                                                                validator:
                                                                                    (
                                                                                        _,
                                                                                        value,
                                                                                    ) => {
                                                                                        if (
                                                                                            value ===
                                                                                                undefined ||
                                                                                            value ===
                                                                                                null ||
                                                                                            value ===
                                                                                                ''
                                                                                        ) {
                                                                                            return Promise.reject(
                                                                                                new Error(
                                                                                                    'Amount is required for partial refund',
                                                                                                ),
                                                                                            );
                                                                                        }
                                                                                        if (
                                                                                            value <=
                                                                                            0
                                                                                        ) {
                                                                                            return Promise.reject(
                                                                                                new Error(
                                                                                                    'Amount must be greater than $0',
                                                                                                ),
                                                                                            );
                                                                                        }
                                                                                        if (
                                                                                            value >
                                                                                            (detail?.originalAmount ||
                                                                                                DEFAULT_TEXT_VISIT_AMOUNT)
                                                                                        ) {
                                                                                            return Promise.reject(
                                                                                                new Error(
                                                                                                    'Amount must be less than or equal to the original amount',
                                                                                                ),
                                                                                            );
                                                                                        }
                                                                                        return Promise.resolve();
                                                                                    },
                                                                            },
                                                                        ]}
                                                                    >
                                                                        <div
                                                                            className={
                                                                                s.refundAmountSection
                                                                            }
                                                                        >
                                                                            <div
                                                                                className={
                                                                                    s.amountLabel
                                                                                }
                                                                            >
                                                                                Refund
                                                                                amount
                                                                            </div>
                                                                            <div
                                                                                className={
                                                                                    s.amountInputRow
                                                                                }
                                                                            >
                                                                                <InputNumber
                                                                                    min={0}
                                                                                    max={detail?.originalAmount || DEFAULT_TEXT_VISIT_AMOUNT}
                                                                                    step={0.01}
                                                                                    precision={2}
                                                                                    prefix="$"
                                                                                    ref={amountInputRef}
                                                                                    className={s.amountInput}
                                                                                    placeholder="0.00"
                                                                                    disabled={!hasRefundPermission}
                                                                                />
                                                                                <span
                                                                                    className={
                                                                                        s.ofAmountText
                                                                                    }
                                                                                >
                                                                                    of
                                                                                    $
                                                                                    {detail?.originalAmount?.toFixed(
                                                                                        2,
                                                                                    ) ||
                                                                                        DEFAULT_TEXT_VISIT_AMOUNT.toFixed(
                                                                                            2,
                                                                                        )}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    </Form.Item>
                                                                </div>
                                                            ) : null;
                                                        }}
                                                    </Form.Item>
                                                )}
                                            </div>
                                        );
                                    })}
                                </Radio.Group>
                            </Form.Item>

                            {/* Form Fields */}
                            <div className={s.fieldWithHelp}>
                                <div className={s.labelRow}>
                                    <div className={s.label}>
                                        Internal notes{' '}
                                        <span className={s.optional}>
                                            (optional)
                                        </span>
                                    </div>
                                </div>
                                <div className={s.help}>
                                    Private notes for internal review and
                                    record-keeping.
                                </div>
                                <Form.Item
                                    name="notesInternal"
                                    className={s.noMargin}
                                >
                                    <Input.TextArea
                                        rows={5}
                                        maxLength={1000}
                                        placeholder="Add internal reasoning, policy reference, or case context…"
                                        disabled={!hasRefundPermission}
                                    />
                                </Form.Item>
                            </div>

                            <div className={s.fieldWithHelp}>
                                <div className={s.labelRow}>
                                    <div className={s.label}>
                                        Notes to patient{' '}
                                        <span className={s.optional}>
                                            (optional)
                                        </span>
                                    </div>
                                </div>
                                <div className={s.help}>
                                    This message will be sent to the patient to
                                    explain the decision.
                                </div>
                                <Form.Item
                                    name="notesToPatient"
                                    className={s.noMargin}
                                >
                                    <Input.TextArea
                                        rows={5}
                                        maxLength={500}
                                        placeholder="Explain the decision to the patient, e.g. 'Your refund was approved because…"
                                        disabled={!hasRefundPermission}
                                    />
                                </Form.Item>
                            </div>

                            <div className={s.fieldWithHelp}>
                                <div className={s.labelRow}>
                                    <div className={s.label}>
                                        Notes to provider{' '}
                                        <span className={s.optional}>
                                            (optional)
                                        </span>
                                    </div>
                                </div>
                                <div className={s.help}>
                                    This message will be sent to the provider to
                                    explain the decision and any payout impact.
                                </div>
                                <Form.Item
                                    name="notesToProvider"
                                    className={s.noMargin}
                                >
                                    <Input.TextArea
                                        rows={5}
                                        maxLength={500}
                                        placeholder="Explain the decision and payout impact to the provider, e.g. 'This case was refunded, payout has been adjusted."
                                        disabled={!hasRefundPermission}
                                    />
                                </Form.Item>
                            </div>
                        </Form>
                    )}
                </div>

                {/* Actions Section (only for review state) */}
                {hasRefund && !isApproved && hasRefundPermission && (
                    <div className={s.actions}>
                        {!isLastItem && (
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={loading}
                                className={s.primaryGreen}
                                onClick={() => {
                                    setSubmitAction('next');
                                    form.submit();
                                }}
                            >
                                Submit & review next refund request
                            </Button>
                        )}
                        <Button
                            htmlType="submit"
                            loading={loading}
                            className={s.secondaryWhite}
                            onClick={() => {
                                setSubmitAction('home');
                                form.submit();
                            }}
                        >
                            {isLastItem ? 'Submit' : 'Submit & return home'}
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Refund;
